import { ArrowLeft, Save } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import FieldLabel from "../components/FieldLabel";
import RelationshipPropertyRow, {
  RelationshipRowEntry,
} from "../components/RelationshipPropertyRow";
import TagField from "../components/TagField";
import TypeaheadUserInput from "../components/TypeaheadUserInput";
import { CRA_CLASSIFICATION_OPTIONS } from "../constants/craClassification";
import { productFieldDescriptions } from "../constants/productFieldDescriptions";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import {
  isAdmin,
  ProductTagInput,
  ProductUpdate,
  RelationshipInput,
  RelationshipTypeDefinition,
  TagDefinitionCategory,
  User as UserType,
} from "../types";

export default function EditProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ProductUpdate>({
    name: "",
    businessArea: "",
    division: "",
    description: "",
    category: "",
  });
  const [selectedTags, setSelectedTags] = useState<ProductTagInput[]>([]);
  const [relationships, setRelationships] = useState<
    (RelationshipInput & { _targetName?: string })[]
  >([]);

  // Fetch business areas
  const { data: businessAreasResponse, isLoading: loadingBusinessAreas } =
    useQuery("businessAreas", () => apiService.getBusinessAreas(), {
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    });

  // Fetch divisions when business area is selected
  const { data: divisionsResponse, isLoading: loadingDivisions } = useQuery(
    ["divisions", formData.businessArea],
    () => apiService.getDivisions(formData.businessArea!),
    {
      enabled: !!formData.businessArea,
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    },
  );

  // Fetch users for the responsibility dropdowns
  const { data: usersResponse, isLoading: loadingUsers } = useQuery(
    "users",
    () => apiService.getUsers(),
    {
      select: (response) =>
        response.data.success ? (response.data.data as UserType[]) : [],
    },
  );

  // Fetch tag definitions
  const { data: tagDefinitions } = useQuery(
    "tagDefinitions",
    () => apiService.getTagDefinitions(),
    {
      select: (response) =>
        response.data.success
          ? (response.data.data as TagDefinitionCategory[])
          : [],
    },
  );

  // Fetch relationship type definitions
  const { data: relationshipTypes } = useQuery(
    "relationshipTypes",
    () => apiService.getRelationshipTypes(),
    {
      select: (response) =>
        response.data.success
          ? (response.data.data as RelationshipTypeDefinition[])
          : [],
    },
  );

  // Fetch product details
  const {
    data: productData,
    isLoading,
    error,
  } = useQuery(["product", id], () => apiService.getProduct(id!), {
    enabled: !!id,
  });

  // Update form data when product is loaded
  useEffect(() => {
    if (productData?.data?.data) {
      const product = (productData.data as any).data;
      setFormData({
        name: product.name || "",
        businessArea: product.businessArea || "",
        division: product.division || "",
        description: product.description || "",
        category: product.category || "",
        apmNumber: product.apmNumber,
        productResponsibleId: product.productResponsibleId,
        businessAccountableId: product.businessAccountableId,
        sdlResponsibleId: product.sdlResponsibleId,
        developmentResponsibility: product.developmentResponsibility,
        customization: product.customization,
        lifecycleStatus: product.lifecycleStatus,
        craClassification: product.craClassification,
        keyProduct: product.keyProduct,
      });
      if (product.productTags) {
        setSelectedTags(
          product.productTags.map((t: any) => ({ key: t.key, value: t.value })),
        );
      }
      if (product.productRelationships) {
        setRelationships(
          product.productRelationships
            .filter((r: any) => r.sourceId === id)
            .map((r: any) => ({
              targetProductId: r.targetId,
              type: r.type,
              criticality: r.criticality || undefined,
              since: r.since ? r.since.substring(0, 10) : undefined,
              until: r.until ? r.until.substring(0, 10) : undefined,
              notes: r.notes || undefined,
              _targetName: r.targetProduct?.name || "",
            })),
        );
      }
    }
  }, [productData]);

  // Reset division when business area changes (but not during initial load)
  useEffect(() => {
    if (productData?.data?.data && formData.businessArea && formData.division) {
      const product = (productData.data as any).data;
      // Only reset if the business area has actually changed from the original
      if (formData.businessArea !== product.businessArea) {
        setFormData((prev) => ({ ...prev, division: "" }));
      }
    }
  }, [formData.businessArea, productData]);

  const updateProductMutation = useMutation(
    (data: ProductUpdate) => apiService.updateProduct(id!, data),
    {
      onSuccess: (response) => {
        if (response.data.success) {
          queryClient.invalidateQueries(["products"]);
          queryClient.invalidateQueries(["product", id]);
          toast.success("Product updated successfully!");
          navigate(`/products/${id}`);
        } else {
          toast.error(response.data.error || "Failed to update product");
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to update product");
      },
    },
  );

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Special handlers for responsibility fields
  const handleProductResponsibleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      productResponsibleId: value,
    }));
  };

  const handleBusinessAccountableChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      businessAccountableId: value,
    }));
  };

  const handleSdlResponsibleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      sdlResponsibleId: value,
    }));
  };

  const handleTagChange = (keyId: string, values: string[]) => {
    setSelectedTags((prev) => [
      ...prev.filter((t) => t.key !== keyId),
      ...values.map((value) => ({ key: keyId, value })),
    ]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.businessArea || !formData.division) {
      toast.error("Please fill in all required fields");
      return;
    }

    updateProductMutation.mutate({
      ...formData,
      tags: selectedTags,
      relationships: relationships.map(({ _targetName, ...r }) => r),
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading product details...</p>
      </div>
    );
  }

  if (error || !productData?.data?.data) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Product not found
        </h3>
        <p className="text-gray-600 mb-4">
          The product you're trying to edit doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate("/products")}
          className="btn btn-primary"
        >
          Back to Products
        </button>
      </div>
    );
  }

  const product = (productData.data as any).data;

  // Check if user has permission to edit this product
  if (!isAdmin(user) && product.ownerId !== user?.id) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Access Denied
        </h3>
        <p className="text-gray-600 mb-4">
          You don't have permission to edit this product.
        </p>
        <button
          onClick={() => navigate(`/products/${id}`)}
          className="btn btn-primary"
        >
          Back to Product Details
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/products/${id}`)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Product</h1>
            <p className="text-gray-600">Update product information</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div className="md:col-span-2">
                <FieldLabel
                  htmlFor="name"
                  description={productFieldDescriptions.name}
                >
                  Product Name *
                </FieldLabel>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="input"
                  placeholder="Enter product name"
                />
              </div>

              {/* Business Area */}
              <div>
                <FieldLabel
                  htmlFor="businessArea"
                  description={productFieldDescriptions.businessArea}
                >
                  Business Area *
                </FieldLabel>
                <select
                  id="businessArea"
                  name="businessArea"
                  value={formData.businessArea}
                  onChange={handleInputChange}
                  required
                  className="input"
                  disabled={loadingBusinessAreas}
                >
                  <option value="">Select a business area</option>
                  {businessAreasResponse?.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              {/* Division */}
              <div>
                <FieldLabel
                  htmlFor="division"
                  description={productFieldDescriptions.division}
                >
                  Division/Business Unit *
                </FieldLabel>
                <select
                  id="division"
                  name="division"
                  value={formData.division}
                  onChange={handleInputChange}
                  required
                  className="input"
                  disabled={!formData.businessArea || loadingDivisions}
                >
                  <option value="">
                    {!formData.businessArea
                      ? "Select a business area first"
                      : loadingDivisions
                        ? "Loading divisions/business units..."
                        : "Select a division/business unit"}
                  </option>
                  {divisionsResponse?.map((division) => (
                    <option key={division} value={division}>
                      {division}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <FieldLabel
                  htmlFor="category"
                  description={productFieldDescriptions.category}
                >
                  Category
                </FieldLabel>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  maxLength={20}
                  className="input"
                  placeholder="e.g., Software, Hardware, Service"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <FieldLabel
                  htmlFor="description"
                  description={productFieldDescriptions.description}
                >
                  Description
                </FieldLabel>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="input"
                  placeholder="Enter product description"
                />
              </div>
            </div>
          </div>

          {/* Product Details Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Product Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* APM Number */}
              <div>
                <FieldLabel
                  htmlFor="apmNumber"
                  description={productFieldDescriptions.apmNumber}
                >
                  APM Number
                </FieldLabel>
                <input
                  type="text"
                  id="apmNumber"
                  name="apmNumber"
                  value={formData.apmNumber || ""}
                  onChange={handleInputChange}
                  maxLength={30}
                  className="input"
                  placeholder="Enter APM number"
                />
              </div>

              {/* Development Responsibility */}
              <div>
                <FieldLabel
                  htmlFor="developmentResponsibility"
                  description={
                    productFieldDescriptions.developmentResponsibility
                  }
                >
                  Development Responsibility
                </FieldLabel>
                <select
                  id="developmentResponsibility"
                  name="developmentResponsibility"
                  value={formData.developmentResponsibility || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select development responsibility</option>
                  <option value="Sandvik">Sandvik</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Sub-Contractor">Sub-Contractor</option>
                </select>
              </div>

              {/* Customization */}
              <div>
                <FieldLabel
                  htmlFor="customization"
                  description={productFieldDescriptions.customization}
                >
                  Customization
                </FieldLabel>
                <select
                  id="customization"
                  name="customization"
                  value={formData.customization || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select customization type</option>
                  <option value="Customized">Customized</option>
                  <option value="COTS">COTS</option>
                </select>
              </div>

              {/* Lifecycle Status */}
              <div>
                <FieldLabel
                  htmlFor="lifecycleStatus"
                  description={productFieldDescriptions.lifecycleStatus}
                >
                  Lifecycle Status
                </FieldLabel>
                <select
                  id="lifecycleStatus"
                  name="lifecycleStatus"
                  value={formData.lifecycleStatus || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select lifecycle status</option>
                  <option value="Planned">Planned</option>
                  <option value="In Development">In Development</option>
                  <option value="Released">Released</option>
                  <option value="Retired">Retired</option>
                  <option value="Obsolete">Obsolete</option>
                </select>
              </div>

              {tagDefinitions
                ?.flatMap((category) => category.keys)
                .map((tagKey) => (
                  <div key={tagKey.id}>
                    <FieldLabel
                      htmlFor={`tag-${tagKey.id}`}
                      description={tagKey.description}
                    >
                      {tagKey.label}
                    </FieldLabel>
                    <TagField
                      id={`tag-${tagKey.id}`}
                      tagKey={tagKey}
                      selectedValues={selectedTags
                        .filter((t) => t.key === tagKey.id)
                        .map((t) => t.value)}
                      onChange={(values) => handleTagChange(tagKey.id, values)}
                    />
                  </div>
                ))}
            </div>
          </div>

          {/* Links Section */}
          {relationshipTypes && relationshipTypes.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Links
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Link this product to other products.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {relationshipTypes.flatMap((typeDef) => {
                  const outgoingEntries: RelationshipRowEntry[] = relationships
                    .filter((r) => r.type === typeDef.id)
                    .map((r, i) => ({
                      key: `out:${r.targetProductId}:${i}`,
                      otherProductId: r.targetProductId,
                      otherProductName: r._targetName || "",
                      isOutgoing: true,
                      criticality: r.criticality,
                      since: r.since,
                      until: r.until,
                      notes: r.notes,
                    }));

                  // Incoming edges are read-only and managed on the other
                  // product's page; we pull them straight from the server
                  // response without tracking in local state.
                  const allRels =
                    (productData?.data as any)?.data?.productRelationships ||
                    [];
                  const incomingEntries: RelationshipRowEntry[] = allRels
                    .filter(
                      (r: any) => r.targetId === id && r.type === typeDef.id,
                    )
                    .map((r: any) => ({
                      key: r.id,
                      otherProductId: r.sourceProduct.id,
                      otherProductName: r.sourceProduct.name,
                      isOutgoing: false,
                      managedOnProductId: r.sourceProduct.id,
                      managedOnProductName: r.sourceProduct.name,
                      criticality: r.criticality || undefined,
                      since: r.since ? r.since.substring(0, 10) : undefined,
                      until: r.until ? r.until.substring(0, 10) : undefined,
                      notes: r.notes || undefined,
                    }));

                  const handleOutgoingChange = (
                    next: RelationshipRowEntry[],
                  ) => {
                    const nextForType = next.map((e) => ({
                      targetProductId: e.otherProductId,
                      type: typeDef.id,
                      criticality: e.criticality,
                      since: e.since,
                      until: e.until,
                      notes: e.notes,
                      _targetName: e.otherProductName,
                    }));
                    setRelationships((prev) => [
                      ...prev.filter((r) => r.type !== typeDef.id),
                      ...nextForType,
                    ]);
                  };

                  // Asymmetric: two separate rows (outgoing editable, inverse
                  // read-only).
                  if (typeDef.inverseLabel) {
                    const rows: JSX.Element[] = [
                      <RelationshipPropertyRow
                        key={`${typeDef.id}:out`}
                        typeDef={typeDef}
                        entries={outgoingEntries}
                        currentProductId={id!}
                        isEditing={true}
                        canEdit={true}
                        hideDirectionGlyph
                        onOutgoingChange={handleOutgoingChange}
                      />,
                    ];
                    if (incomingEntries.length > 0) {
                      rows.push(
                        <RelationshipPropertyRow
                          key={`${typeDef.id}:in`}
                          typeDef={typeDef}
                          labelOverride={typeDef.inverseLabel}
                          entries={incomingEntries}
                          currentProductId={id!}
                          isEditing={false}
                          canEdit={false}
                          hideDirectionGlyph
                          onOutgoingChange={() => {}}
                        />,
                      );
                    }
                    return rows;
                  }

                  // Symmetric: single combined row.
                  const entries = [...outgoingEntries, ...incomingEntries];
                  return [
                    <RelationshipPropertyRow
                      key={typeDef.id}
                      typeDef={typeDef}
                      entries={entries}
                      currentProductId={id!}
                      isEditing={true}
                      canEdit={true}
                      onOutgoingChange={handleOutgoingChange}
                    />,
                  ];
                })}
              </div>
            </div>
          )}

          {/* Responsibilities Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Responsibilities
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Product owner */}
              <div>
                <FieldLabel
                  htmlFor="productResponsibleId"
                  description={productFieldDescriptions.productResponsibleId}
                >
                  Product owner
                </FieldLabel>
                <TypeaheadUserInput
                  id="productResponsibleId"
                  name="productResponsibleId"
                  value={formData.productResponsibleId || ""}
                  onChange={handleProductResponsibleChange}
                  users={usersResponse || []}
                  loading={loadingUsers}
                  placeholder="Enter email or select user"
                />
              </div>

              {/* Business Accountable */}
              <div>
                <FieldLabel
                  htmlFor="businessAccountableId"
                  description={productFieldDescriptions.businessAccountableId}
                >
                  Business Accountable
                </FieldLabel>
                <TypeaheadUserInput
                  id="businessAccountableId"
                  name="businessAccountableId"
                  value={formData.businessAccountableId || ""}
                  onChange={handleBusinessAccountableChange}
                  users={usersResponse || []}
                  loading={loadingUsers}
                  placeholder="Enter email or select user"
                />
              </div>

              {/* SDL Responsible */}
              <div>
                <FieldLabel
                  htmlFor="sdlResponsibleId"
                  description={productFieldDescriptions.sdlResponsibleId}
                >
                  SDL Responsible
                </FieldLabel>
                <TypeaheadUserInput
                  id="sdlResponsibleId"
                  name="sdlResponsibleId"
                  value={formData.sdlResponsibleId || ""}
                  onChange={handleSdlResponsibleChange}
                  users={usersResponse || []}
                  loading={loadingUsers}
                  placeholder="Enter email or select user"
                />
              </div>
            </div>
          </div>

          {/* Classification Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Classification
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* CRA Classification */}
              <div>
                <FieldLabel
                  htmlFor="craClassification"
                  description={productFieldDescriptions.craClassification}
                >
                  CRA Classification
                </FieldLabel>
                <select
                  id="craClassification"
                  name="craClassification"
                  value={formData.craClassification || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select CRA classification</option>
                  {CRA_CLASSIFICATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Key Product */}
              <div>
                <FieldLabel
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
                  description={productFieldDescriptions.keyProduct}
                >
                  Key Product
                </FieldLabel>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="keyProduct"
                    name="keyProduct"
                    checked={formData.keyProduct || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                  />
                  <label
                    htmlFor="keyProduct"
                    className="ml-2 text-sm text-gray-900 dark:text-gray-100"
                  >
                    Mark as key product
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/products/${id}`)}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateProductMutation.isLoading}
              className="btn btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              {updateProductMutation.isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
